import * as React from "react";
import { IP_BLOCK_HOURS, IP_ORDERS_PER_HOUR } from "@ewn/shared";
import { liftBlock, listBlocks, type AdminBlockRow } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Notice } from "@/components/ui/feedback";
import { ConfirmModal } from "@/components/ui/modal";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TableShell, TBody, TD, TEmpty, TH, THead, TR } from "@/components/ui/table";

/**
 * Networks the order allowance has blocked: `IP_ORDERS_PER_HOUR` orders in an
 * hour, then `IP_BLOCK_HOURS` hours of none.
 *
 * Most are abuse. Some are one busy mobile network, where many honest buyers
 * share an address -- a customer writing in is how that shows. Lifting ends the
 * block now and starts the network's count again from zero.
 */
export function BlocksScreen() {
  const { data, loading, error, reload } = useAsync(() => listBlocks(), []);
  const action = useAction();
  const [lifting, setLifting] = React.useState<AdminBlockRow | null>(null);
  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Blocked networks"
        description={`Networks that opened ${IP_ORDERS_PER_HOUR} orders in an hour, blocked for ${IP_BLOCK_HOURS} hours.`}
      />

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}

      <Card>
        {loading && rows.length === 0 ? (
          <SkeletonRows rows={3} cols={5} />
        ) : (
          <TableShell>
            <THead>
              <TH>Network</TH>
              <TH numeric>Orders in the hour</TH>
              <TH>Blocked since</TH>
              <TH>Blocked until</TH>
              <TH />
            </THead>
            <TBody>
              {rows.length === 0 && <TEmpty colSpan={5}>No network is blocked.</TEmpty>}
              {rows.map((row) => (
                <TR key={row.net}>
                  <TD className="font-mono text-xs">{row.net}</TD>
                  <TD numeric>{row.order_count}</TD>
                  <TD className="whitespace-nowrap text-muted-foreground">{when(row.blocked_at)}</TD>
                  <TD className="whitespace-nowrap">{when(row.blocked_until)}</TD>
                  <TD>
                    <Button size="sm" variant="outline" disabled={action.busy} onClick={() => setLifting(row)}>
                      Unblock
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableShell>
        )}
      </Card>

      <ConfirmModal
        open={lifting !== null}
        title="Unblock this network?"
        confirmLabel="Unblock"
        busy={action.busy}
        onClose={() => setLifting(null)}
        body={
          <p>
            <span className="font-mono">{lifting?.net}</span> can open orders again now, and its
            count of orders in the hour starts from zero.
          </p>
        }
        onConfirm={async () => {
          if (lifting) await action.run(() => liftBlock(lifting.net));
          setLifting(null);
          reload();
        }}
      />
    </div>
  );
}
